/**
 * Kleda Client App - Voorinspectie Components
 * Premium, minimalist design for inspection results and upsells
 */

import { useState, useEffect, useRef } from 'react'
import type { OrderData, VoorinspectieItem, RandReparatieData, OndertapijtData } from '../types'
import { confirmRandReparatie, refreshPaymentLink } from '../api'
import {
  trackVoorinspectieViewed,
  trackRandReparatieViewed,
  trackRandReparatieConfirmed,
  trackOndertapijtViewed,
  trackPhotoClicked,
  trackInfoExpanded
} from '../analytics'
import { Card, Icon, NummerBadge, StatusHeader, FaqList, type FaqItem } from './UI'
import { fetchVoorinspectieThumbnails } from '../api'


// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Format price in Dutch locale
 */
function formatPrice(amount: number | undefined): string {
  if (amount === undefined || amount === null) return '€ 0,00'
  return `€ ${amount.toFixed(2).replace('.', ',')}`
}

/**
 * Format m² in Dutch locale (rounded to 2 decimals)
 */
function formatM2(m2: number | undefined): string {
  if (m2 === undefined || m2 === null) return '0 m²'
  // Round to 2 decimals to avoid floating point precision issues
  const rounded = Math.round(m2 * 100) / 100
  return `${rounded.toFixed(2).replace('.', ',')} m²`
}

/**
 * Parse voorinspectie multi-select values
 */
function parseVoorinspectieValues(value: string | undefined): string[] {
  if (!value) return []
  return value.split(';').map(v => v.trim()).filter(Boolean)
}

// =============================================================================
// INSPECTIE ITEM CARD
// =============================================================================

interface InspectieItemCardProps {
  item: VoorinspectieItem
  index: number
  thumbnailUrl?: string
  /** Eigen /fotos/{token}-timeline (media-laag); wint van de CompanyCam-link */
  fotoLink?: string
}

function InspectieItemCard({ item, index, thumbnailUrl, fotoLink }: InspectieItemCardProps) {
  const observations = parseVoorinspectieValues(item.voorinspectie)
  // Eigen infra eerst, CompanyCam (Foto_s__c) als vangnet voor oude kleden
  const fotosHref = fotoLink || item.fotoUrl

  // Stille maatregel in overzicht-stijl: "6,00 m² (300×200 cm) · Pakket"
  const dimensions = item.diameter && item.diameter > 1
    ? `(ø${item.diameter.toString().replace('.', ',')} cm)`
    : item.lengte && item.breedte
      ? `(${item.lengte.toString().replace('.', ',')}×${item.breedte.toString().replace('.', ',')} cm)`
      : null

  // item.name is meestal het generieke "Vloerkleed" — alleen tonen als het
  // iets toevoegt aan "Vloerkleed N"
  const toonNaam = item.name && item.name.toLowerCase() !== 'vloerkleed'

  return (
    <Card className="mb-md">
      <div className="flex items-start gap-md">
        {/* Thumbnail van het kleed zelf (CompanyCam) — klik door naar alle foto's */}
        {thumbnailUrl && (
          <a
            href={fotosHref || thumbnailUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackPhotoClicked(item.id, item.name)}
            className="flex-shrink-0"
          >
            <img
              src={thumbnailUrl}
              alt={`Foto van vloerkleed ${index + 1}`}
              loading="lazy"
              className="w-20 h-20 object-cover rounded-kleda-md"
            />
          </a>
        )}

        <div className="flex-1 min-w-0">
          {/* Kop: nummer-badge + naam + stille maatregel (prijs leeft in het overzicht) */}
          <h3 className="flex items-center gap-xs text-[15px] font-semibold text-ink">
            <NummerBadge nummer={index + 1} />
            <span className="truncate">
              Vloerkleed
              {toonNaam && <span className="text-ink-muted font-normal"> · {item.name}</span>}
            </span>
          </h3>
          <p className="text-[13px] text-ink-faint mt-xs">
            {formatM2(item.m2)}{dimensions ? ` ${dimensions}` : ''}
            {item.pakketNaam ? ` · ${item.pakketNaam}` : ''}
            {item.heeftOndertapijt ? ' · Ondertapijt' : ''}
          </p>
          {item.extras && item.extras.length > 0 && (
            <p className="text-[13px] text-ink-soft">
              {item.extras.join(' · ')}
            </p>
          )}

          {/* Photo Link */}
          {fotosHref && (
            <a
              href={fotosHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackPhotoClicked(item.id, item.name)}
              className="inline-flex items-center gap-xs mt-sm text-primary hover:text-primary-dark text-body-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              Bekijk foto's
            </a>
          )}
        </div>
      </div>

      {/* Observaties — eerlijk verwachtingsmanagement, rustig gebracht */}
      {observations.length > 0 && (
        <div className="mt-md pt-md border-t border-hairline">
          <p className="section-label mb-xs">
            Wat we zagen
          </p>
          <ul className="space-y-xs">
            {observations.map((obs, i) => (
              <li key={i} className="text-body-sm text-ink-soft flex items-start gap-xs">
                <span className="text-ink-faint mt-0.5">·</span>
                <span>{obs}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Item remarks */}
      {item.voorinspectieOpmerkingen && (
        <div className="mt-md pt-md border-t border-hairline">
          <p className="section-label mb-xs">
            Opmerking van de inspecteur
          </p>
          <p className="text-body-sm text-ink-soft italic">
            "{item.voorinspectieOpmerkingen}"
          </p>
        </div>
      )}
    </Card>
  )
}

// =============================================================================
// UITLEG BIJ OBSERVATIES (Diep vuil, Looppad, Vlekken) — als stille accordion
// =============================================================================

const UITLEG_TYPES: Record<string, 'diep_vuil' | 'looppad' | 'vlekken'> = {
  'Wat is diep vuil?': 'diep_vuil',
  'Wat is een looppad?': 'looppad',
  'Wat als vlekken niet helemaal weggaan?': 'vlekken',
}

const UITLEG_DIEP_VUIL: FaqItem = {
  vraag: 'Wat is diep vuil?',
  antwoord: 'Diep vuil is los vuil of stof tussen de polen of bolletjes wol van het kleed dat soms zo diep zit weggezakt dat het onmogelijk voor ons is om dit geheel te verwijderen. Als wij het kleed te hard kloppen kan er schade aan het kleed ontstaan en dat is iets wat wij uiteraard niet willen.',
}

const UITLEG_LOOPPAD: FaqItem = {
  vraag: 'Wat is een looppad?',
  antwoord: "Looppad is een vorm van slijtage, vaak herkenbaar aan wat 'platliggende', iets versleten vezels rondom een plek waar normaal bijvoorbeeld een tafel staat. Of plekken waar het meest gelopen wordt. Dit is een vorm van slijtage en blijft vaak zichtbaar na de reiniging.",
}

const UITLEG_VLEKKEN: FaqItem = {
  vraag: 'Wat als vlekken niet helemaal weggaan?',
  antwoord: 'Jammer genoeg kunnen sommige vlekken waarschijnlijk niet helemaal verwijderd worden, vooral als deze vlekken helemaal in de vezel getrokken zijn en daarom een verkleuring veroorzaakt hebben. We zullen onze uiterste best doen ook deze vlekken te verwijderen maar dit lukt niet altijd.',
}

// =============================================================================
// RAND REPARATIE CARD
// =============================================================================

interface RandReparatieCardProps {
  data: RandReparatieData
  token: string
  werkOrderId: string
  onSuccess: () => void
  /** itemId → volgnummer in het rapport, voor de nummer-badges */
  volgnummers?: Record<string, number>
  /** itemId → thumbnail-url (CompanyCam), zodat duidelijk is welk kleed het is */
  thumbnails?: Record<string, string>
}

export function RandReparatieCard({ data, token, werkOrderId, onSuccess, volgnummers, thumbnails }: RandReparatieCardProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(data.items.map(item => item.id))
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const hasTrackedView = useRef(false)

  // Track view once
  useEffect(() => {
    if (!hasTrackedView.current && data.eligible && !data.alreadyOrdered) {
      trackRandReparatieViewed(werkOrderId, data.totalPriceInclVat, data.items.length)
      hasTrackedView.current = true
    }
  }, [data, werkOrderId])

  // Calculate dynamic total based on selection
  const selectedTotal = data.items
    .filter(item => selectedIds.has(item.id))
    .reduce((sum, item) => sum + (item.randReparatiePrijs || 0), 0)

  const toggleItem = (id: string) => {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      setError('Selecteer minimaal één vloerkleed')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await confirmRandReparatie({
        token,
        confirm: true,
        itemIds: Array.from(selectedIds)
      })

      if (response.success) {
        setSuccess(true)
        trackRandReparatieConfirmed(werkOrderId, selectedTotal, Array.from(selectedIds))
        // Betaallink synchroon bijwerken vóór de refetch (zie refreshPaymentLink).
        await refreshPaymentLink(token)
        onSuccess()
      } else {
        setError(response.message || 'Er ging iets mis')
      }
    } catch {
      setError('Er ging iets mis. Probeer het later opnieuw.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Don't show if not eligible or already ordered
  if (!data.eligible || data.alreadyOrdered) {
    if (data.alreadyOrdered) {
      return (
        <Card className="border-2 border-secondary/30">
          <div className="flex items-center gap-sm text-secondary">
            <Icon name="check" />
            <div>
              <p className="text-body font-medium text-ink">Randreparatie toegevoegd</p>
              <p className="text-body-sm text-ink-muted">Je bestelling bevat randreparatie</p>
            </div>
          </div>
        </Card>
      )
    }
    return null
  }

  // Success state
  if (success) {
    return (
      <Card className="border-2 border-secondary">
        <div className="text-center py-md">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/10 text-secondary mb-sm">
            <Icon name="check" />
          </div>
          <p className="text-body font-medium text-ink">Randreparatie toegevoegd!</p>
          <p className="text-body-sm text-ink-muted mt-xs">
            Dit wordt meegenomen in je bestelling
          </p>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <h3 className="text-body font-bold text-ink mb-xs">
        Randreparatie
      </h3>
      <p className="text-body-sm text-ink-soft mb-lg">
        Onze inspecteur zag dat de randen loslaten. Dit kan door de reiniging erger worden — wij kunnen deze opnieuw voor je lijmen.
      </p>

      {/* Item Selection */}
      <p className="section-label mb-sm">
        Selecteer vloerkleden
      </p>
      <div className="space-y-sm mb-lg">
        {data.items.map(item => (
          <label
            key={item.id}
            className={`
              flex items-center justify-between p-md rounded-kleda-md cursor-pointer transition-all
              ${selectedIds.has(item.id)
                ? 'bg-primary/5 border-2 border-primary'
                : 'bg-surface-soft border-2 border-transparent hover:border-border-subtle'
              }
            `}
          >
            <div className="flex items-center gap-sm">
              <input
                type="checkbox"
                checked={selectedIds.has(item.id)}
                onChange={() => toggleItem(item.id)}
                className="w-5 h-5 rounded border-border-subtle text-primary focus:ring-primary"
              />
              {thumbnails?.[item.id] && (
                <img
                  src={thumbnails[item.id]}
                  alt={`Foto van vloerkleed ${volgnummers?.[item.id] ?? ''}`}
                  loading="lazy"
                  className="w-10 h-10 object-cover rounded-kleda-sm flex-shrink-0"
                />
              )}
              <div>
                <p className="flex items-center gap-xs text-body font-medium text-ink">
                  {volgnummers?.[item.id] != null && <NummerBadge nummer={volgnummers[item.id]} />}
                  <span>{item.name}</span>
                </p>
                <p className="text-body-sm text-ink-muted">{formatM2(item.m2)}</p>
              </div>
            </div>
            <p className="text-body font-semibold text-primary">
              {formatPrice(item.randReparatiePrijs)}
            </p>
          </label>
        ))}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between py-md border-t border-hairline mb-lg">
        <p className="text-body font-semibold text-ink">Totaal randreparatie</p>
        <p className="text-heading-sm font-bold text-primary">{formatPrice(selectedTotal)}</p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body">
          {error}
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleConfirm}
        disabled={isSubmitting || selectedIds.size === 0}
        className="btn-primary w-full"
      >
        {isSubmitting ? 'Bezig...' : 'Toevoegen aan bestelling →'}
      </button>

    </Card>
  )
}

// =============================================================================
// ONDERTAPIJT CARD
// =============================================================================

interface OndertapijtCardProps {
  data: OndertapijtData
  werkOrderId: string
  highlighted?: boolean
}

export function OndertapijtCard({ data, werkOrderId, highlighted = false }: OndertapijtCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const hasTrackedView = useRef(false)

  // Track view once
  useEffect(() => {
    if (!hasTrackedView.current && data.eligible && !data.alreadyOrdered) {
      trackOndertapijtViewed(
        werkOrderId,
        data.totalPriceWithDiscountInclVat,
        data.savingsInclVat,
        data.items.length
      )
      hasTrackedView.current = true
    }
  }, [data, werkOrderId])

  // Scroll into view and animate if highlighted
  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      cardRef.current.classList.add('animate-pulse-once')
    }
  }, [highlighted])

  // Navigate to dedicated ondertapijt page
  const goToOndertapijtPage = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('type', 'ondertapijt')
    window.location.href = url.toString()
  }

  // Don't show if not eligible or already ordered
  if (!data.eligible || data.alreadyOrdered) {
    if (data.alreadyOrdered) {
      return (
        <Card className="border-2 border-secondary/30">
          <div className="flex items-center gap-sm text-secondary">
            <Icon name="check" />
            <div>
              <p className="text-body font-medium text-ink">Ondertapijt toegevoegd</p>
              <p className="text-body-sm text-ink-muted">Je bestelling bevat ondertapijt op maat</p>
            </div>
          </div>
        </Card>
      )
    }
    return null
  }

  return (
    <div
      ref={cardRef}
      className={`
        card transition-all
        ${highlighted ? 'ring-2 ring-primary/20' : ''}
      `}
    >
      <h3 className="text-body font-bold text-ink mb-xs">
        Kleda Base ondertapijt op maat
      </h3>
      <p className="text-body-sm text-ink-soft mb-md">
        Professioneel ondertapijt met tweezijdige grip (OEKO-TEX) — beschermt je vloer, voorkomt schuiven. Op maat gesneden voor jouw {data.items.length === 1 ? 'vloerkleed' : `${data.items.length} vloerkleden`}.
      </p>

      {/* Prijs — korting rustig gebracht */}
      <div className="flex items-baseline justify-between bg-surface-soft rounded-kleda-md p-md mb-sm">
        <p className="text-body-sm text-ink-muted">
          {data.discountPercentage}% korting — omdat je {data.items.length === 1 ? 'kleed' : 'kleden'} toch al in onze wasserij {data.items.length === 1 ? 'is' : 'zijn'}
        </p>
        <p className="whitespace-nowrap ml-sm">
          <span className="text-body-sm text-ink-faint line-through mr-xs">{formatPrice(data.totalPriceInclVat)}</span>
          <span className="text-heading-sm font-bold text-ink">{formatPrice(data.totalPriceWithDiscountInclVat)}</span>
        </p>
      </div>

      {/* Op maat / definitief — vooraf duidelijk, als stille regel */}
      <p className="text-[13px] text-ink-faint mb-md">
        Op maat gemaakt — kan na bestelling niet geannuleerd of geruild worden.
      </p>

      {/* CTA → naar de pagina waar je per kleed kiest en bewust bevestigt */}
      <button
        onClick={goToOndertapijtPage}
        className="btn-primary w-full mb-sm"
      >
        Ondertapijt bekijken →
      </button>

      <p className="text-body-sm text-ink-muted text-center">
        Je kiest per vloerkleed en bevestigt op de volgende pagina
      </p>
    </div>
  )
}

// =============================================================================
// VOORINSPECTIE VIEW
// =============================================================================

interface VoorinspectieViewProps {
  order: OrderData
  token: string
  highlightOndertapijt?: boolean
  onRefresh: () => void
}

export function VoorinspectieView({ order, token, highlightOndertapijt = false, onRefresh }: VoorinspectieViewProps) {
  const hasTrackedView = useRef(false)

  const voorinspectie = order.voorinspectie
  const items = voorinspectie?.items ?? []
  const enkelKleed = items.length <= 1

  // Calculate total m² from voorinspectie items only (excludes ondertapijt, extras etc.)
  const totalM2Vloerkleden = items.reduce((sum, item) => sum + (item.m2 || 0), 0)

  // Kleed-thumbnails + eigen foto-timeline-links laden async bij — het
  // rapport rendert direct
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({})
  const [fotoLinks, setFotoLinks] = useState<Record<string, string>>({})
  useEffect(() => {
    let active = true
    fetchVoorinspectieThumbnails(token).then(result => {
      if (active) {
        setThumbnails(result.thumbnails)
        setFotoLinks(result.fotoLinks)
      }
    })
    return () => { active = false }
  }, [token])

  // itemId → volgnummer, voor consistente nummer-badges in rapport en advies
  const volgnummers: Record<string, number> = {}
  items.forEach((item, i) => { volgnummers[item.id] = i + 1 })

  // Track page view once
  useEffect(() => {
    if (!hasTrackedView.current && voorinspectie) {
      trackVoorinspectieViewed(
        order.werkOrderId,
        order.werkOrderNummer,
        totalM2Vloerkleden,
        voorinspectie.items.length
      )
      hasTrackedView.current = true
    }
  }, [order, voorinspectie, totalM2Vloerkleden])

  if (!voorinspectie) {
    return null
  }

  // Uitleg-accordion: alleen de observatie-typen die op deze order spelen
  const uitlegItems: FaqItem[] = [
    ...(voorinspectie.infoDiepVuil ? [UITLEG_DIEP_VUIL] : []),
    ...(voorinspectie.infoLooppad ? [UITLEG_LOOPPAD] : []),
    ...(voorinspectie.infoEnkeleVlekken ? [UITLEG_VLEKKEN] : []),
  ]

  const heeftAdvies = Boolean(
    (voorinspectie.randReparatie?.eligible || voorinspectie.randReparatie?.alreadyOrdered) ||
    (voorinspectie.ondertapijt?.eligible || voorinspectie.ondertapijt?.alreadyOrdered)
  )

  return (
    <div>
      <StatusHeader
        title={enkelKleed ? 'Je kleed is geïnspecteerd.' : 'Je kleden zijn geïnspecteerd.'}
        subtitle={
          <>
            We hebben {enkelKleed ? 'je kleed' : 'alles'} nagemeten en goed bekeken.
            Hieronder zie je per kleed wat onze inspecteur zag — zodat je weet wat je
            kunt verwachten — {heeftAdvies ? 'en ons advies' : 'en wat er nu gebeurt'}.
          </>
        }
      />

      <p className="-mt-sm mb-lg text-[13px] text-ink-faint">
        Nagemeten: <span className="text-ink-soft">{formatM2(totalM2Vloerkleden)} totaal</span>
      </p>

      {/* Per kleed: maat + foto's + observaties */}
      {items.map((item, index) => (
        <InspectieItemCard
          key={item.id}
          item={item}
          index={index}
          thumbnailUrl={thumbnails[item.id]}
          fotoLink={fotoLinks[item.id]}
        />
      ))}

      {/* Algemene opmerking van de inspecteur */}
      {voorinspectie.algemeenOpmerkingen && (
        <Card className="mb-md">
          <p className="section-label mb-xs">
            Opmerking van de inspecteur
          </p>
          <p className="text-body-sm text-ink-soft whitespace-pre-line italic">
            "{voorinspectie.algemeenOpmerkingen}"
          </p>
        </Card>
      )}

      {/* Uitleg bij de observaties — stille accordion, één bron */}
      {uitlegItems.length > 0 && (
        <FaqList
          items={uitlegItems}
          title="Wat betekent dit?"
          className="mb-lg"
          onOpen={(vraag) => {
            const type = UITLEG_TYPES[vraag]
            if (type) trackInfoExpanded(type)
          }}
        />
      )}

      {/* Ons advies na de inspectie */}
      {heeftAdvies && (
        <p className="section-label mb-sm mt-lg">Ons advies na de inspectie</p>
      )}

      {voorinspectie.randReparatie && (
        <div className="mb-md">
          <RandReparatieCard
            data={voorinspectie.randReparatie}
            token={token}
            werkOrderId={order.werkOrderId}
            onSuccess={onRefresh}
            volgnummers={volgnummers}
            thumbnails={thumbnails}
          />
        </div>
      )}

      {voorinspectie.ondertapijt && (
        <div className="mb-md">
          <OndertapijtCard
            data={voorinspectie.ondertapijt}
            werkOrderId={order.werkOrderId}
            highlighted={highlightOndertapijt}
          />
        </div>
      )}

      {/* What's Next */}
      <Card className="mb-md">
        <p className="section-label mb-sm">Wat nu</p>
        <p className="text-body-sm text-ink-soft mb-sm">
          We gaan {enkelKleed ? 'je vloerkleed' : 'je vloerkleden'} nu professioneel reinigen.
          Als we klaar zijn ontvang je een e-mail om een aflevermoment te kiezen.
        </p>
        <p className="text-body-sm text-ink-soft">
          Bij de afleverbevestiging kun je eenvoudig online betalen via iDEAL. Liever contant of pin? Geen probleem — je kunt ook ter plekke betalen bij onze chauffeur.
        </p>
      </Card>
    </div>
  )
}
